<template>
  <div class="container-fluid">
    <div class="bg-wheel py-3 position-relative">
      <h1 class="text-white text-center" style="font-family: 'SSGLT'">
        单词大作战 🐍
      </h1>
      <div
        class="score-board  text-white p-3 rounded my-3 text-center"
      >
        得分: <span>{{ score }}</span> | 关卡: <span>{{ level }}</span>
      </div>
      <div class="controls d-flex flex-wrap gap-3 justify-content-center my-3">
        <button
          class="btn btn-success"
          @click="startGame"
          :disabled="!wordData.length || isPlaying"
        >
          🚀 开始游戏
        </button>
        <button class="btn btn-success" @click="downloadTemplate">
          📥 下载模板
        </button>
        <button class="btn btn-success" @click="restartGame">
          🔄 重新开始
        </button>
        <button class="btn btn-success" @click="toggleMusic">
          {{ musicBtnText }}
        </button>
        <label class="btn btn-success mb-0">
          📁 上传单词表
          <input
            type="file"
            accept=".xlsx,.xls"
            @change="handleFileChange"
            hidden
          />
        </label>
      </div>
      <!-- 包含 canvas 的父容器设置了固定高度 -->
      <div
        class="d-flex flex-column justify-content-center align-items-center canvas-container"
        style="height: 600px"   
      >
        <canvas
          ref="gameCanvas"
          id="myCanvas"
          class="border rounded shadow mx-auto"
        ></canvas>
      </div>
      <!-- 游戏结束提示区域，用 v-if 条件渲染 -->
      <div
        v-if="gameOverVisible"
        class="position-absolute top-50 start-50 translate-middle bg-dark p-4 rounded text-center"
      >
        <h2 v-if="isPerfect" style="color: white">🎉 完美通关！</h2>
        <h2 v-else style="color: white">💪 继续努力！</h2>
        <p style="color: white">当前得分: {{ score }}</p>
        <button class="btn btn-success" @click="restartGame">重新挑战</button>
      </div>
      <!-- 底部装饰 -->
      <img
        src="@/assets/images/Nezha2.png"
        class="corner-decoration bottom-0 start-0"
      />
      <img
        src="@/assets/images/Nezha2.png"
        class="corner-decoration bottom-0 end-0 image-flip"
      />
    </div>
  </div>
</template>
  
  <script setup>
import { ref, onMounted, onBeforeUnmount } from "vue";
import * as XLSX from "xlsx";

// Canvas 以及 2D 上下文
const gameCanvas = ref(null);
let ctx = null;

// 控制游戏结束提示显示
const gameOverVisible = ref(false);
const isPerfect = ref(false);

// 游戏状态、分数、关卡、单词数据
const isPlaying = ref(false);
const score = ref(0);
const level = ref(1);
const wordData = ref([]);
let currentTheme = "";
let expectedGood = 6;
let finalLevel = false;
let correctEaten = 0;

// 蛇的状态
let snake = [];
let currentWords = [];
let dx = 0,
  dy = 0;

// 背景音乐及按钮文字
const musicBtnText = ref("🎵 关闭音乐");
const bgMusic = new Audio(require("@/assets/NewPiano.mp3"));
bgMusic.loop = true;

// 蛇头图片
const snakeImage = new Image();
snakeImage.src = require("@/assets/images/tanchishe.png");

let gameLoop = null;

// 工具函数：Fisher-Yates 洗牌算法
function shuffle(array) {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
  return array;
}

// 初始化游戏数据
function initGame() {
  snake = [{ x: 400, y: 300 }];
  dx = 0;
  dy = 0;
  correctEaten = 0;
  generateLevelWords();
  if (gameLoop) clearInterval(gameLoop);
  gameLoop = setInterval(update, 150);
  // 隐藏游戏结束提示
  gameOverVisible.value = false;
}

// 生成当前关卡单词
function generateLevelWords() {
  currentWords = [];
  finalLevel = false;
  const good = wordData.value.flatMap((cat) => cat.good);
  const bad = wordData.value.flatMap((cat) => cat.bad);
  if (good.length < 6 || bad.length < 4) {
    alert("单词库中的单词数量不足，可能会导致重复或关卡提前结束！");
  }
  const selectedGood = shuffle([...good]).slice(0, 6);
  expectedGood = selectedGood.length;
  if (expectedGood < 6) finalLevel = true;
  const selectedBad = shuffle([...bad]).slice(0, 4);
  const positions = new Set();
  const getPosition = () => ({
    x: Math.floor(Math.random() * (gameCanvas.value.width - 100)) + 50,
    y: Math.floor(Math.random() * (gameCanvas.value.height - 100)) + 50,
  });
  selectedGood.forEach((word) => {
    let pos;
    do {
      pos = getPosition();
    } while (positions.has(`${pos.x},${pos.y}`));
    positions.add(`${pos.x},${pos.y}`);
    currentWords.push({ ...pos, word, type: "good" });
  });
  selectedBad.forEach((word) => {
    let pos;
    do {
      pos = getPosition();
    } while (positions.has(`${pos.x},${pos.y}`));
    positions.add(`${pos.x},${pos.y}`);
    currentWords.push({ ...pos, word, type: "bad" });
  });
}

// 游戏主循环
function update() {
  if (!isPlaying.value) return;
  // 使用 canvas 实际的宽高，而非硬编码 800/600
  const canvasW = gameCanvas.value.width;
  const canvasH = gameCanvas.value.height;
  const head = { x: snake[0].x + dx, y: snake[0].y + dy };
  //碰撞单词大小
  const fontSize = gameCanvas.value.width * 0.03;
  const collisionThreshold = fontSize * 1.1; // 或其他合适比例
  // 边界穿越，使用动态宽高
  head.x = (head.x + canvasW) % canvasW;
  head.y = (head.y + canvasH) % canvasH;

  let ateFood = false;
  currentWords.forEach((word, index) => {
    if (
      Math.abs(head.x - word.x) < collisionThreshold &&
      Math.abs(head.y - word.y) < collisionThreshold
    ) {
      if (word.type === "good") {
        score.value++;
        correctEaten++;
        ateFood = true;
        if (correctEaten === expectedGood) {
          levelComplete();
        }
      } else {
        score.value--;
        gameOver(false);
      }
      currentWords.splice(index, 1);
    }
  });
  snake.unshift(head);
  if (!ateFood) {
    snake.pop();
  }
  draw();
}

// 绘制 canvas 内容
function draw() {
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, gameCanvas.value.width, gameCanvas.value.height);
  // 主题文字
  ctx.fillStyle = "#8a030a";
  ctx.textAlign = "left";
  ctx.textBaseline = "top";
  ctx.font = "48px Arial";
  
  // 绘制边框
  ctx.strokeStyle = "#8a030a";
  ctx.lineWidth = 2;
  ctx.strokeText("主题: " + currentTheme, 20, 40);
  // 绘制填充文字
  ctx.fillText("主题: " + currentTheme, 20, 40);
  // 绘制蛇身（从第二个分段开始）
  for (let i = 1; i < snake.length; i++) {
    ctx.beginPath();
    ctx.arc(snake[i].x, snake[i].y, 10, 0, Math.PI * 2);
    ctx.fillStyle = "#ffc8ca";
    ctx.fill();
  }
  // 绘制蛇头，旋转显示（根据 dx, dy 确定角度）
  ctx.save();
  const head = snake[0];
  let angle = Math.atan2(dy, dx);
  if (dx === 0 && dy === 0) {
    angle = 0;
  }
  angle += Math.PI / 2;
  ctx.translate(head.x, head.y);
  ctx.rotate(angle);
  ctx.drawImage(snakeImage, -10, -10, 20, 20);
  ctx.restore();
  // 绘制单词
  currentWords.forEach((word) => {
    ctx.fillStyle = "#000000";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.font = `${gameCanvas.value.width * 0.03}px Arial`;
    ctx.fillText(word.word, word.x, word.y);
  });
}

function levelComplete() {
  if (finalLevel || score.value === level.value * 6) {
    createConfetti();
    gameOver(true);
  } else {
    level.value++;
    correctEaten = 0;
    generateLevelWords();
  }
}

function gameOver(perfect) {
  clearInterval(gameLoop);
  isPlaying.value = false;
  bgMusic.pause();
  isPerfect.value = perfect;
  // 使用响应式变量控制提示显示
  gameOverVisible.value = true;
}

function createConfetti() {
  document.querySelectorAll(".confetti").forEach((c) => c.remove());
  for (let i = 0; i < 50; i++) {
    const confetti = document.createElement("div");
    confetti.className = "confetti";
    confetti.style.left = Math.random() * 100 + "vw";
    confetti.style.animation = `fall ${Math.random() * 3 + 2}s linear`;
    setTimeout(() => confetti.remove(), 3000);
    document.body.appendChild(confetti);
  }
}

function downloadTemplate() {
  const template = [
    {
      分类主题: "水果",
      正确单词: "apple,banana,pear",
      错误单词: "desk,tomato,photo",
    },
  ];
  const ws = XLSX.utils.json_to_sheet(template);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "模板");
  XLSX.writeFile(wb, "单词模板.xlsx");
}

function restartGame() {
  gameOverVisible.value = false;
  score.value = 0;
  level.value = 1;
  isPlaying.value = true;
  initGame();
}

function startGame() {
  if (!wordData.value.length) {
    alert("请先上传单词表！");
    return;
  }
  isPlaying.value = true;
  score.value = 0;
  level.value = 1;
  initGame();
  bgMusic.play();
}

function toggleMusic() {
  if (bgMusic.paused) {
    bgMusic.play();
    musicBtnText.value = "🎵 关闭音乐";
  } else {
    bgMusic.pause();
    musicBtnText.value = "🎵 开启音乐";
  }
}

function handleFileChange(e) {
  const file = e.target.files[0];
  if (!file) return;
  const reader = new FileReader();
  reader.onload = function (e) {
    try {
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: "array" });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const expectedHeaders = ["分类主题", "正确单词", "错误单词"];
      const actualHeaders = XLSX.utils.sheet_to_json(sheet, { header: 1 })[0];
      if (!expectedHeaders.every((h, i) => h === actualHeaders[i])) {
        alert("文件格式错误！请使用下载的模板文件");
        return;
      }
      wordData.value = XLSX.utils.sheet_to_json(sheet).map((row) => ({
        category: row["分类主题"],
        good: row["正确单词"].split(",").map((s) => s.trim()),
        bad: row["错误单词"].split(",").map((s) => s.trim()),
      }));
      currentTheme = wordData.value.map((row) => row.category).join(" ");
    } catch (error) {
      alert("文件解析失败，请检查文件格式");
    }
  };
  reader.readAsArrayBuffer(file);
}

// 定义键盘事件处理函数
function keyHandler(e) {
  if (["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(e.key)) {
    e.preventDefault();
  }
  if (!isPlaying.value) return;
  switch (e.key) {
    case "ArrowUp":
      if (dy !== 20) {
        dx = 0;
        dy = -20;
      }
      break;
    case "ArrowDown":
      if (dy !== -20) {
        dx = 0;
        dy = 20;
      }
      break;
    case "ArrowLeft":
      if (dx !== 20) {
        dx = -20;
        dy = 0;
      }
      break;
    case "ArrowRight":
      if (dx !== -20) {
        dx = 20;
        dy = 0;
      }
      break;
  }
}

// 处理窗口大小变化
function handleResize() {
  if (gameCanvas.value) {
    // 获取父容器的宽度和高度
    const containerWidth = gameCanvas.value.parentElement.clientWidth;
    const containerHeight = gameCanvas.value.parentElement.clientHeight;
    
    // 设置 canvas 的宽度和高度，减去边距
    gameCanvas.value.width = containerWidth - 40; // 减去左右边距

    if (isPlaying.value) {
      draw();
    }
  }
}

onMounted(() => {
  ctx = gameCanvas.value.getContext("2d");
  // 设置初始尺寸（若父容器固定高度，可使用父容器实际值，如600）
  gameCanvas.value.width = gameCanvas.value.clientWidth;
  gameCanvas.value.height = gameCanvas.value.clientHeight;
  window.addEventListener("resize", handleResize);
  handleResize();
  document.addEventListener("keydown", keyHandler);
});

onBeforeUnmount(() => {
  if (gameLoop) clearInterval(gameLoop);
  document.removeEventListener("keydown", keyHandler);
  window.removeEventListener("resize", handleResize);
});
</script>
  
  <style scoped>
@import "@/assets/font/font.css";
.corner-decoration {
  position: absolute;
  width: 120px;
  z-index: 100;
}
.bg-wheel {
  background: #8a030a;
}

@keyframes fall {
  to {
    transform: translateY(100vh) rotate(360deg);
  }
}
.confetti {
  position: absolute;
  width: 10px;
  height: 10px;
  background: #f1c40f;
  pointer-events: none;
}
#myCanvas {
  width: 90%;
  height: 100%;
}
</style>
  